import * as Sentry from "@sentry/react";
import {
  compact,
  filter,
  find,
  flatMap,
  includes,
  isEmpty,
  map,
  reject,
  sortBy,
  sumBy,
  toLower,
  uniq,
} from "lodash/fp";

import { CONFERENCING_PLATFORMS_BY_ID } from "@readai/common/constants/platforms";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@readai/common/constants/providers";
import ROUTES from "@readai/common/routes";
import {
  ApiChannelProvider,
  ApiProductivityChannelProvider,
  ApiV1DetailedTopic,
  ApiV1EntityActor,
  ApiV1Topic,
  ApiV1TopicEntity,
  ApiV1TopicEntityCitation,
  BaseTopicChannelProvider,
  Topic,
  TopicChannelProvider,
  TopicEntity,
  TopicEntityContributor,
  TopicKind,
} from "@readai/common/types";
import { ConferencingMeetingPlatform } from "@readai/common/types";
import { ProductivityProviderId } from "@readai/common/types/providerTypes";
import {
  optionalParseUTCDateTime,
  parseUTCDateTimeFromString,
} from "@readai/common/utils/datetimeUtils";
import { isEmailProvider } from "@readai/common/utils/providerUtils";
import * as TopicUtils from "@readai/common/utils/topicUtils";
import {
  getUniqueContributors,
  isUnknownOrAggregateProvider,
} from "@readai/common/utils/topicUtils";
import { getSpeakerName } from "@readai/common/utils/transcriptUtils";

const MULTI_SOURCE_TOPIC_KINDS: TopicKind[] = [
  "multi_source_external_domain",
  "multi_source_semantic_external_domain",
  "multi_source_semantic",
];

export const API_PRODUCTIVITY_CHANNEL_PROVIDER_TO_PROVIDER: {
  [key in ApiProductivityChannelProvider]: ProductivityProviderId;
} = {
  gmail: PRODUCTIVITY_PROVIDERS_BY_ID.google_email.id,
  outlook: PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_email.id,
  slack: PRODUCTIVITY_PROVIDERS_BY_ID.slack_ingest.id,
  teams_channel:
    PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.id,
  teams_chat: PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.id,
};

export const API_CHANNEL_PROVIDER_TO_PROVIDER: {
  [key in ApiChannelProvider]:
    | ProductivityProviderId
    | ConferencingMeetingPlatform;
} = {
  ...API_PRODUCTIVITY_CHANNEL_PROVIDER_TO_PROVIDER,
  meet: CONFERENCING_PLATFORMS_BY_ID.meet.id,
  mobile: CONFERENCING_PLATFORMS_BY_ID.mobile.id,
  teams: CONFERENCING_PLATFORMS_BY_ID.teams.id,
  teams_web: CONFERENCING_PLATFORMS_BY_ID.teams.id,
  webex: CONFERENCING_PLATFORMS_BY_ID.webex.id,
  zoom: CONFERENCING_PLATFORMS_BY_ID.zoom.id,
};

const getUniqueKey = () =>
  `${Math.round(Math.random() * Math.random() * 100000000)}`;

const getProvider = (provider: ApiChannelProvider): TopicChannelProvider =>
  API_CHANNEL_PROVIDER_TO_PROVIDER[provider] ?? "unknown";

const getAnonymousUserId = () => `anonymous_${getUniqueKey()}`;

const parseV1EntityActor = (
  data: ApiV1EntityActor,
): TopicEntityContributor => ({
  id: data.id,
  kind: data.kind,
  displayName: getSpeakerName(data.providerDisplayName),
  provider: getProvider(data.provider),
  providerUserId: data.providerUserId,
  userId: data.userId ?? getAnonymousUserId(),
});

const parseProviderStats = (
  providerStats: ApiV1DetailedTopic["providerStats"],
): Topic["providerMetadata"] => {
  if (isEmpty(providerStats?.citationMetrics)) return {};

  return Object.entries(providerStats.citationMetrics).reduce(
    (obj, [provider, metadata]) => {
      const providerName =
        API_CHANNEL_PROVIDER_TO_PROVIDER[provider as ApiChannelProvider];
      obj[providerName] = metadata;
      return obj;
    },
    {} as Topic["providerMetadata"],
  );
};

const getTopicProviders = (apiProviders: ApiChannelProvider[]) => {
  const providers = uniq(map(getProvider, apiProviders));
  const baseProviders = reject(
    isUnknownOrAggregateProvider,
    providers,
  ) as BaseTopicChannelProvider[];
  return sortBy(
    (p) => toLower(TopicUtils.getProviderDisplayName(p) ?? ""),
    baseProviders,
  );
};

const isDetailedTopic = (
  topic: ApiV1Topic | ApiV1DetailedTopic,
): topic is ApiV1DetailedTopic => topic != null && "actors" in topic;

const getMessageCount = (
  providerMetadata: Topic["providerMetadata"],
  topicProvider: Topic["provider"],
) => {
  if (isEmpty(providerMetadata)) return 0;
  if (isUnknownOrAggregateProvider(topicProvider)) {
    return sumBy(
      (metadata) => metadata.messageCount,
      Object.values(providerMetadata),
    );
  }
  return providerMetadata[topicProvider]?.messageCount ?? 0;
};

const getChannelCount = (
  providerMetadata: Topic["providerMetadata"],
  topicProvider: Topic["provider"],
) => {
  if (isEmpty(providerMetadata)) return 0;
  if (isUnknownOrAggregateProvider(topicProvider)) {
    return Object.entries(providerMetadata).reduce(
      (sum, [provider, metadata]) => {
        const providerChannelCount = isEmailProvider(provider)
          ? metadata.threadCount
          : metadata.channelCount;
        return sum + providerChannelCount;
      },
      0,
    );
  }

  return (
    (isEmailProvider(topicProvider)
      ? providerMetadata[topicProvider]?.threadCount
      : providerMetadata[topicProvider]?.channelCount) ?? 0
  );
};

export const parseV1Topic = (data: ApiV1Topic | ApiV1DetailedTopic): Topic => {
  if (isEmpty(data)) {
    throw new Error("Unable to fetch topic data.");
  }

  const isDetailed = isDetailedTopic(data);

  const channelProviders = getTopicProviders(data.providers);

  const isMultiSourceTopic =
    includes(data.kind, MULTI_SOURCE_TOPIC_KINDS) ||
    channelProviders.length > 1;

  const primaryProvider: TopicChannelProvider = isMultiSourceTopic
    ? "multi"
    : (channelProviders[0] ?? "unknown");

  const providerMetadata = isDetailed
    ? parseProviderStats(data.providerStats)
    : {};

  return {
    id: data.id,
    title: data.titleOverride ?? data.title,
    channelCount: getChannelCount(providerMetadata, primaryProvider),
    channelProviders,
    contributors: isDetailed
      ? getUniqueContributors(map(parseV1EntityActor, data.actors))
      : [],
    provider: primaryProvider,
    isHidden: data.isHidden,
    isMultiSourceTopic,
    firstMention: parseUTCDateTimeFromString(data.firstMentionedOn),
    lastMention: parseUTCDateTimeFromString(data.lastMentionedOn),
    impersonationCount: data.impersonationCount ?? 0,
    keywords: data.keywords,
    kind: data.kind,
    messageCount: getMessageCount(providerMetadata, primaryProvider),
    owner: data.owner,
    providerMetadata,
    status: "active",
    summary: data.summary,
    version: "1",
  };
};

export const parseV1Topics = (data: ApiV1Topic[]): Topic[] =>
  map(parseV1Topic, data);

const parseV1Citations =
  (parentEntityId: TopicEntity["entityId"]) =>
  (data: ApiV1TopicEntityCitation): TopicEntity => {
    const contributors = map(parseV1EntityActor, data.actors);
    const sessionId =
      data.source.internalSourceKind === "session"
        ? data.source.internalSourceId
        : undefined;

    return {
      entityId: data.id,
      kind: "citation",
      author: find({ kind: "author" }, contributors),
      channelId: data.source.internalSourceId,
      children: [],
      citations: [],
      contributors,
      createdOn: parseUTCDateTimeFromString(data.createdOn),
      startTime: parseUTCDateTimeFromString(data.startTime),
      endTime: parseUTCDateTimeFromString(data.endTime),
      parentEntityId,
      provider: getProvider(data.source.provider),
      providerLink: sessionId
        ? ROUTES.meetingReportById(sessionId)
        : data.providerLink,
      providerSourceDisplayName: data.source.providerSourceName,
      rank: 0, // citations don't have or need ranks
      sessionId,
      text: data.text ?? "",
      updatedOn: parseUTCDateTimeFromString(data.updatedOn),
    };
  };

export const parseV1TopicEntity = (
  data: ApiV1TopicEntity,
): TopicEntity | null => {
  try {
    const childEntityKind =
      data.kind === "key_question" ? "key_question_answer" : undefined;

    const entityProviders = uniq(
      map(
        (citation) =>
          API_CHANNEL_PROVIDER_TO_PROVIDER[citation.source.provider],
        data.citations,
      ),
    );

    const entityActors: ApiV1EntityActor[] = isEmpty(data.actors)
      ? flatMap("actors", data.citations)
      : data.actors;

    const contributors = getUniqueContributors(
      map(parseV1EntityActor, entityActors),
    );

    const childEntities = childEntityKind
      ? filter({ kind: childEntityKind }, data.children)
      : [];

    const citations = map(parseV1Citations(data.id), data.citations);
    const defaultCitation: TopicEntity | undefined = citations[0];

    const entityStartTime = data.startTime ?? defaultCitation.startTime;
    const entityEndTime =
      data.endTime ?? defaultCitation?.endTime ?? entityStartTime;
    const createdOn = parseUTCDateTimeFromString(data.createdOn);

    return {
      entityId: data.id,
      kind: data.kind,
      author: find({ kind: "author" }, contributors),
      channelId: defaultCitation?.channelId ?? "",
      children: compact(map(parseV1TopicEntity, childEntities)),
      citations,
      contributors,
      createdOn,
      startTime:
        optionalParseUTCDateTime(entityStartTime ?? entityEndTime) ?? createdOn,
      endTime: optionalParseUTCDateTime(entityEndTime) ?? createdOn,
      provider: entityProviders[0] ?? "unknown",
      providerLink: defaultCitation?.sessionId
        ? ROUTES.meetingReportById(defaultCitation.sessionId)
        : defaultCitation?.providerLink,
      providerSourceDisplayName:
        defaultCitation?.providerSourceDisplayName ?? "",
      rank: data.entityRank ?? 0,
      sessionId: defaultCitation?.sessionId,
      text: data.text,
      updatedOn: parseUTCDateTimeFromString(data.updatedOn),
    };
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const parseV1TopicEntities = (data: ApiV1TopicEntity[]): TopicEntity[] =>
  compact(map(parseV1TopicEntity, data));
